import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from 'formik'

import EditForm from '../../../../../../features/components/Forms/EditForm'
import MultiLevelSelect from '../../../../../../components/MultiLevelSelect'
import GenderContent from './ContentSections/GenderContent'
import AgeContent from './ContentSections/AgeContent'
import FieldLabelWrapper from '../../../../../../features/components/Form/FieldLabelWrapper'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { useEditSectionFormSubmit } from '../../../../../../hooks/formHooks/useEditSectionFormSubmit'

import { filterObjectEmptyValues } from '../../../../../../helpers/common'
import { getMultiLevelSelectPayloadValues } from '../../../../../../components/MultiLevelSelect/formatters'
import { formatValuesToFE } from './formatters'

import { updateLineItem, clearUpdateLineItem } from '../../../../../../modules/actions/lineItems'
import {
  lineItemUpdateErrorSelector,
  lineItemUpdateIsLoadingSelector,
  lineItemWasUpdatedSelector
} from '../../../../../../modules/selectors/lineItems'
import { selectedTikTokAdAccountSelector } from '../../../../../../modules/selectors/adAccounts'
import {
  tikTokRegionsSelector,
  tikTokRegionsWasLoadedSelector
} from '../../../../../../modules/selectors/tikTokConstants'

import { AGE } from '../../../../../Multiplatform/LineItemForms/fields'
import { TIKTOK_PLATFORM } from '../../../../../../constants/selectLists/platformList'
import { HISTORY_META_EDIT_SECTION } from '../../../../../../constants/changeHistory'
import { GEOLOCATIONS } from '../../fields'
import { GENDER_OPTION } from '../../../../../ReusableFormFields/GenderFields/fields'
import { validationSchema } from './validation'

const DemographicsSectionForm = ({ editItemData: lineItem, title, ...formProps }) => {
  const dispatch = useDispatch()

  const lineItemWasUpdated = useSelector(lineItemWasUpdatedSelector)
  const tikTokRegions = useSelector(tikTokRegionsSelector)
  const tikTokRegionsWasLoaded = useSelector(tikTokRegionsWasLoadedSelector)
  const { id: tiktokAdAccountId } = useSelector(selectedTikTokAdAccountSelector)

  const allowLocationsEdit = tikTokRegionsWasLoaded

  const formattedRegionOptions = useMemo(
    () =>
      tikTokRegions.map(region => ({
        ...region,
        parent: region['parent_id']
      })),
    [tikTokRegions]
  )

  const flatToNestedRegionsSortFunction = useCallback((a, b) => a.name.localeCompare(b.name), [])

  const initialValues = useMemo(
    () => formatValuesToFE({ lineItem, allowLocationsEdit, formattedRegionOptions }),
    [lineItem, formattedRegionOptions, allowLocationsEdit]
  )

  const handleSubmit = useCallback(
    values => {
      // as we use PUT for update tiktok campaign, we should pass full data in payload
      dispatch(
        updateLineItem(
          {
            ...filterObjectEmptyValues(lineItem),
            account: tiktokAdAccountId,
            age: Object.keys(values[AGE]).filter(range => values[AGE][range]),
            gender: values[GENDER_OPTION],
            ...(allowLocationsEdit && {
              location: getMultiLevelSelectPayloadValues(values[GEOLOCATIONS], tikTokRegions, 'parent_id')
            }),
            meta: {
              [HISTORY_META_EDIT_SECTION]: title
            }
          },
          lineItem.id,
          TIKTOK_PLATFORM,
          {
            requestMethod: 'PUT'
          }
        )
      )
    },
    [dispatch, lineItem, tikTokRegions, tiktokAdAccountId, allowLocationsEdit, title]
  )

  const { onSubmit, manualSuccessSubmit } = useEditSectionFormSubmit({ initialValues, handleSubmit })

  const formik = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true
  })
  const purifiedFormik = usePurifiedFormik(formik)

  const { setFieldValue, values } = formik

  const handleClearUpdateLineItem = useCallback(() => {
    dispatch(clearUpdateLineItem())
  }, [dispatch])

  return (
    <EditForm
      formik={purifiedFormik}
      initialValues={initialValues}
      successSubmit={lineItemWasUpdated || manualSuccessSubmit}
      clearEditItem={handleClearUpdateLineItem}
      errorSelector={lineItemUpdateErrorSelector}
      isLoadingSelector={lineItemUpdateIsLoadingSelector}
      {...formProps}
    >
      <AgeContent formik={purifiedFormik} />
      <GenderContent formik={purifiedFormik} />
      {allowLocationsEdit && (
        <FieldLabelWrapper label="Locations">
          <MultiLevelSelect
            placeholder="Add a region"
            setFieldValue={setFieldValue}
            value={values[GEOLOCATIONS]}
            name={GEOLOCATIONS}
            options={formattedRegionOptions}
            flatToNestedArrSortFunction={flatToNestedRegionsSortFunction}
          />
        </FieldLabelWrapper>
      )}
    </EditForm>
  )
}

export default DemographicsSectionForm
